/**
 * Centralized utilities for handling time period columns in the frontend.
 *
 * Supports period types:
 * - day: YYYY_MM_DD (e.g., 2024_01_15)
 * - month: YYYY_MM (e.g., 2024_01)
 * - quarter: YYYY_QN (e.g., 2024_Q1)
 * - year: YYYY (e.g., 2024)
 */

// Period type constants
export const PERIOD_DAY = 'day';
export const PERIOD_MONTH = 'month';
export const PERIOD_QUARTER = 'quarter';
export const PERIOD_YEAR = 'year';

export const VALID_PERIODS = [PERIOD_DAY, PERIOD_MONTH, PERIOD_QUARTER, PERIOD_YEAR];
export const DEFAULT_PERIOD = PERIOD_MONTH;

// Period configuration with display information
export const PERIOD_CONFIG = {
  [PERIOD_DAY]: {
    label: 'Daily',
    description: 'Data indexed by day (e.g., daily transactions)',
    exampleFormat: 'YYYY_MM_DD',
    example: '2024_01_15',
    regex: /^\d{4}_\d{2}_\d{2}$/,
    columnLabel: 'Example Day',
    fieldLabel: 'Day Field',
    mappingKey: 'period',
    structureLabel: 'Daily Metric Column Structure',
  },
  [PERIOD_MONTH]: {
    label: 'Monthly',
    description: 'Data indexed by month (e.g., monthly bordereaux)',
    exampleFormat: 'YYYY_MM',
    example: '2024_01',
    regex: /^\d{4}[-_]\d{2}$/,
    columnLabel: 'Example Month',
    fieldLabel: 'Month Field',
    mappingKey: 'period',
    structureLabel: 'Monthly Metric Column Structure',
  },
  [PERIOD_QUARTER]: {
    label: 'Quarterly',
    description: 'Data indexed by quarter (e.g., quarterly reports)',
    exampleFormat: 'YYYY_QN',
    example: '2024_Q1',
    regex: /^\d{4}_Q[1-4]$/,
    columnLabel: 'Example Quarter',
    fieldLabel: 'Quarter Field',
    mappingKey: 'period',
    structureLabel: 'Quarterly Metric Column Structure',
  },
  [PERIOD_YEAR]: {
    label: 'Yearly',
    description: 'Data indexed by year (e.g., annual summaries)',
    exampleFormat: 'YYYY',
    example: '2024',
    regex: /^\d{4}$/,
    columnLabel: 'Example Year',
    fieldLabel: 'Year Field',
    mappingKey: 'period',
    structureLabel: 'Yearly Metric Column Structure',
  },
};

/**
 * Get the period type from a table schema.
 * @param {Object} tableSchema - The table schema object
 * @returns {string} - The period type (defaults to 'month')
 */
export function getPeriodType(tableSchema) {
  if (!tableSchema) return DEFAULT_PERIOD;

  if (tableSchema.tableType !== 'timeSeries') return DEFAULT_PERIOD;

  const period = tableSchema.timeSeriesConfig?.period;
  if (period && VALID_PERIODS.includes(period)) {
    return period;
  }

  return DEFAULT_PERIOD;
}

/**
 * Get the period configuration for a given period type.
 * @param {string} periodType - The period type
 * @returns {Object} - The period configuration
 */
export function getPeriodConfig(periodType) {
  return PERIOD_CONFIG[periodType] || PERIOD_CONFIG[DEFAULT_PERIOD];
}

/**
 * Check if a column name matches a specific period format.
 * @param {string} columnName - The column name to check
 * @param {string} periodType - The period type to match
 * @returns {boolean} - True if the column matches the period format
 */
export function isPeriodColumn(columnName, periodType) {
  if (typeof columnName !== 'string') return false;
  const config = PERIOD_CONFIG[periodType];
  if (!config) return false;
  return config.regex.test(columnName);
}

/**
 * Check if a column name matches any valid period format.
 * @param {string} columnName - The column name to check
 * @returns {boolean} - True if the column matches any period format
 */
export function isAnyPeriodColumn(columnName) {
  if (typeof columnName !== 'string') return false;

  // Check in order of specificity: day, quarter, month, year
  // (most specific first to avoid false positives)
  const checkOrder = [PERIOD_DAY, PERIOD_QUARTER, PERIOD_MONTH, PERIOD_YEAR];

  for (const periodType of checkOrder) {
    if (PERIOD_CONFIG[periodType].regex.test(columnName)) {
      return true;
    }
  }
  return false;
}

/**
 * Detect the period type from a column name.
 * @param {string} columnName - The column name to analyze
 * @returns {string|null} - The detected period type or null
 */
export function detectPeriodTypeFromColumn(columnName) {
  if (typeof columnName !== 'string') return null;

  // Check in order of specificity: day, quarter, month, year
  const checkOrder = [PERIOD_DAY, PERIOD_QUARTER, PERIOD_MONTH, PERIOD_YEAR];

  for (const periodType of checkOrder) {
    if (PERIOD_CONFIG[periodType].regex.test(columnName)) {
      return periodType;
    }
  }
  return null;
}

// Alias for backward compatibility and shorter name
export const detectPeriodType = detectPeriodTypeFromColumn;

/**
 * Format a period key for display.
 * Converts period keys like '2024_01', '2024_Q1', '2024_01_15', '2024' to human-readable format.
 * @param {string} column - The period key to format
 * @param {string} periodType - Optional period type (auto-detected if not provided)
 * @returns {string} - The formatted period string
 */
export function formatPeriodKey(column, periodType = null) {
  if (typeof column !== 'string') return column;

  // Auto-detect period type if not provided
  const detectedType = periodType || detectPeriodTypeFromColumn(column);

  if (!detectedType) {
    // Not a period column, return as-is with underscores replaced
    return column.replace(/_/g, ' ');
  }

  try {
    switch (detectedType) {
      case PERIOD_DAY: {
        // YYYY_MM_DD -> "15 Jan 24"
        const [year, month, day] = column.split('_');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleString('default', { day: 'numeric', month: 'short', year: '2-digit' });
      }

      case PERIOD_MONTH: {
        // YYYY_MM or YYYY-MM -> "Jan 24"
        const [year, month] = column.split(/[-_]/);
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleString('default', { month: 'short', year: '2-digit' });
      }

      case PERIOD_QUARTER: {
        // YYYY_QN -> "Q1 24"
        const match = column.match(/^(\d{4})_Q([1-4])$/);
        if (match) {
          const year = match[1].slice(-2);
          return `Q${match[2]} ${year}`;
        }
        return column;
      }

      case PERIOD_YEAR: {
        // YYYY -> "2024"
        return column;
      }

      default:
        return column.replace(/_/g, ' ');
    }
  } catch (e) {
    console.warn('Error formatting period key:', column, e);
    return column.replace(/_/g, ' ');
  }
}

/**
 * Parse a period key to a Date object.
 * @param {string} key - The period key to parse
 * @param {string} periodType - Optional period type (auto-detected if not provided)
 * @returns {Date|null} - The parsed Date or null if invalid
 */
export function periodKeyToDate(key, periodType = null) {
  if (typeof key !== 'string') return null;

  const detectedType = periodType || detectPeriodTypeFromColumn(key);
  if (!detectedType) return null;

  try {
    switch (detectedType) {
      case PERIOD_DAY: {
        const [year, month, day] = key.split('_');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }

      case PERIOD_MONTH: {
        const [year, month] = key.split(/[-_]/);
        return new Date(parseInt(year), parseInt(month) - 1, 1);
      }

      case PERIOD_QUARTER: {
        const match = key.match(/^(\d{4})_Q([1-4])$/);
        if (match) {
          const year = parseInt(match[1]);
          const quarter = parseInt(match[2]);
          const month = (quarter - 1) * 3;
          return new Date(year, month, 1);
        }
        return null;
      }

      case PERIOD_YEAR: {
        return new Date(parseInt(key), 0, 1);
      }

      default:
        return null;
    }
  } catch (e) {
    console.warn('Error parsing period key:', key, e);
    return null;
  }
}

/**
 * Sort period columns chronologically.
 * @param {string[]} columns - Array of period column names
 * @param {string} periodType - Optional period type (auto-detected for each column if not provided)
 * @returns {string[]} - Sorted array of column names
 */
export function sortPeriodColumns(columns, periodType = null) {
  if (!Array.isArray(columns)) return [];

  return [...columns].sort((a, b) => {
    const dateA = periodKeyToDate(a, periodType);
    const dateB = periodKeyToDate(b, periodType);

    if (!dateA && !dateB) return a.localeCompare(b);
    if (!dateA) return 1;
    if (!dateB) return -1;

    return dateA - dateB;
  });
}

/**
 * Get the display column key prefix for a table type.
 * For time series tables, this returns 'period' (for use in display_columns like 'period.columnName').
 * For a raw table, which has no metric columns, this returns an empty string.
 * @param {Object} tableSchema - The table schema object
 * @returns {string} - The prefix to use ('period' or '')
 */
export function getDisplayColumnPrefix(tableSchema) {
  return tableSchema?.tableType === 'timeSeries' ? 'period' : '';
}

/**
 * Build the display column key for a value column.
 * @param {string} columnPath - The column path (e.g., 'Premium' or 'Group.SubColumn')
 * @param {Object} tableSchema - The table schema object
 * @returns {string} - The full display column key (e.g., 'period.Premium' or 'Group.SubColumn')
 */
export function buildDisplayColumnKey(columnPath, tableSchema) {
  const prefix = getDisplayColumnPrefix(tableSchema);
  return prefix ? `${prefix}.${columnPath}` : columnPath;
}

/**
 * Get options for period selection dropdown.
 * @returns {Array<{value: string, label: string, description: string}>} - Array of period options
 */
export function getPeriodOptions() {
  return VALID_PERIODS.map(period => ({
    value: period,
    label: PERIOD_CONFIG[period].label,
    description: PERIOD_CONFIG[period].description,
    example: PERIOD_CONFIG[period].example,
  }));
}

/**
 * Get the field label for the period mapping section.
 * @param {string} periodType - The period type
 * @returns {string} - The field label (e.g., "Month Field", "Quarter Field")
 */
export function getPeriodFieldLabel(periodType) {
  const config = PERIOD_CONFIG[periodType] || PERIOD_CONFIG[DEFAULT_PERIOD];
  return config.fieldLabel;
}

/**
 * Get the structure label for the value column builder.
 * @param {string} periodType - The period type
 * @returns {string} - The structure label (e.g., "Monthly Metric Column Structure")
 */
export function getPeriodStructureLabel(periodType) {
  const config = PERIOD_CONFIG[periodType] || PERIOD_CONFIG[DEFAULT_PERIOD];
  return config.structureLabel;
}

/**
 * Get the example column label for schema viewer.
 * @param {string} periodType - The period type
 * @returns {string} - The column label (e.g., "Example Month", "Example Quarter")
 */
export function getPeriodColumnLabel(periodType) {
  const config = PERIOD_CONFIG[periodType] || PERIOD_CONFIG[DEFAULT_PERIOD];
  return config.columnLabel;
}

export default {
  PERIOD_DAY,
  PERIOD_MONTH,
  PERIOD_QUARTER,
  PERIOD_YEAR,
  VALID_PERIODS,
  DEFAULT_PERIOD,
  PERIOD_CONFIG,
  getPeriodType,
  getPeriodConfig,
  isPeriodColumn,
  isAnyPeriodColumn,
  detectPeriodTypeFromColumn,
  detectPeriodType,
  formatPeriodKey,
  periodKeyToDate,
  sortPeriodColumns,
  getDisplayColumnPrefix,
  buildDisplayColumnKey,
  getPeriodOptions,
  getPeriodFieldLabel,
  getPeriodStructureLabel,
  getPeriodColumnLabel,
};
